/**
 * DischargeStep - Discharge instructions step for ChartMind
 * 
 * Auto-generates patient-friendly discharge instructions when mounted
 * Shows sections with modal editing
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  RefreshRounded,
  ForwardToInboxRounded,
  PictureAsPdfOutlined,
} from '@mui/icons-material';
import ConfirmDialog from 'components/common/ConfirmDialog';
import { downloadDischargeInstructionsPdf } from 'services/documentsService';
import DischargeHandoffDialog from './DischargeHandoffDialog';
import SectionCard from './SectionCard';
import ClinicalReferenceSources from './ClinicalReferenceSources';
import { patientContextPayloadKey } from 'utils/patientContext';

// ============================================================================
// Types
// ============================================================================

/** One instruction as the patient receives it (see getPatientDischarge in ChartMindPage). */
interface PatientInstruction {
  title: string;
  category: string;
  prose: string;
  medicationName?: string;
}

interface DischargeStepProps {
  transcript: string;
  selectedDiagnoses: any[];
  selectedTreatments: any[];
  autoGenerate?: boolean;
  // Lifted state from parent
  instructions: any[];
  includedInstructionIds: Set<string>;
  loading: boolean;
  error: string | null;
  isGenerating: boolean;
  hasBeenGenerated: boolean;
  generateDischargeInstructions: (
    transcript: string,
    diagnosis: any,
    treatments: any[],
    patientContext?: any,
  ) => Promise<void>;
  regenerateDischargeInstructions: (
    transcript: string,
    diagnosis: any,
    treatments: any[],
    patientContext?: any,
  ) => Promise<void>;
  toggleInstructionIncluded: (id: string) => void;
  updateInstructionProse: (id: string, prose: string) => void;
  clinicalReferences?: any[];
  embeddedDocuments?: any[];
  /**
   * Imported patient record payload (utils/patientContext). Must be forwarded on every
   * generate call: discharge instructions name specific medications, so this is where a
   * missing allergy does real harm. The previous `patientDemographics` argument was never
   * passed from here, so the hook always ran with an empty object.
   */
  patientContext?: any;
  /**
   * Getting the instructions to the patient. They live here rather than on the Chart step
   * because this is the patient-facing document and the step where it is on screen, and
   * because the patient is usually still in the room now, whereas the note is often written
   * after they have gone.
   */
  // Present when a patient's record was imported: the send is then direct.
  importedPatient?: { patientUserId?: string; patientName?: string | null } | null;
  // Delivers the discharge instructions to the imported patient.
  // Resolves with whether the patient was also emailed (not every patient has an address).
  onSendDischarge?: () => Promise<{ emailed?: boolean } | void>;
  // No imported patient: mints the one-time QR handoff their phone claims.
  onCreateDischargeHandoff?: () => Promise<{ handoffId: string; expiresAt: number }>;
  // The patient-facing instructions: what is sent, and what is printed.
  getPatientDischarge?: () => { instructions: PatientInstruction[]; date?: string };
  // Off for simulated teaching cases, where there is no patient to send to.
  showSendToPatient?: boolean;
  /** Heading override, e.g. for MorningReport's teaching cases. */
  heading?: { title: string; subtitle: string };
}

// ============================================================================
// Main DischargeStep Component
// ============================================================================

const DischargeStep: React.FC<DischargeStepProps> = ({
  transcript,
  selectedDiagnoses,
  selectedTreatments,
  autoGenerate = true,
  // Lifted state from parent
  instructions,
  includedInstructionIds,
  loading,
  error,
  isGenerating,
  hasBeenGenerated,
  generateDischargeInstructions,
  regenerateDischargeInstructions,
  toggleInstructionIncluded,
  updateInstructionProse,
  clinicalReferences = [],
  embeddedDocuments = [],
  patientContext = null,
  importedPatient = null,
  onSendDischarge,
  onCreateDischargeHandoff,
  getPatientDischarge,
  showSendToPatient = true,
  heading,
}) => {
  const theme = useTheme();
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendEmailed, setSendEmailed] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [handoffClaimed, setHandoffClaimed] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const patientLabel = importedPatient?.patientName || 'the patient';
  const canSend = includedInstructionIds.size > 0;

  /**
   * ⚠️ OFFERED WHETHER OR NOT A RECORD WAS IMPORTED. It used to appear only after an import,
   * which most encounters never do, so the feature was invisible. An imported patient gets the
   * direct send (their uid is known and consent is on record); anyone else gets a one-time QR
   * their own phone claims, no account needed.
   */
  const handleSendToPatientClick = useCallback(() => {
    setSendSuccess(false);
    setHandoffClaimed(false);
    if (importedPatient) setSendConfirmOpen(true);
    else setHandoffOpen(true);
  }, [importedPatient]);

  const handleHandoffClaimed = useCallback(() => {
    setHandoffOpen(false);
    setHandoffClaimed(true);
  }, []);

  const handleSendDischarge = useCallback(async () => {
    setSendConfirmOpen(false);
    setSendError(null);
    setSending(true);
    try {
      const result = await onSendDischarge?.();
      setSendEmailed(Boolean(result && result.emailed));
      setSendSuccess(true);
    } catch (err) {
      setSendError(
        err instanceof Error ? err.message : 'Failed to send discharge instructions.',
      );
    } finally {
      setSending(false);
    }
  }, [onSendDischarge]);

  // Same button and same kind of file as the Chart step's export. The PDF is also how
  // these get printed for a patient without a smartphone.
  const handleExportPdf = useCallback(async () => {
    setExportError(null);
    setExportingPdf(true);
    try {
      await downloadDischargeInstructionsPdf(getPatientDischarge?.().instructions || []);
    } catch (err) {
      console.error('Failed to export discharge instructions PDF:', err);
      setExportError(
        err instanceof Error ? err.message : 'Failed to export discharge instructions to PDF.',
      );
    } finally {
      setExportingPdf(false);
    }
  }, [getPatientDischarge]);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalDescription, setModalDescription] = useState('');

  // Fingerprint of the patient context the on-screen instructions were generated against.
  // Stays null until this component instance generates something, so a session hydrated
  // from Firestore (hasBeenGenerated already true) is never regenerated spuriously.
  const generatedForPatientRef = useRef<string | null>(null);
  const patientContextKey = patientContextPayloadKey(patientContext);

  // Auto-generate on mount if not already generated, and regenerate if the patient record
  // arrived after the fact. hasBeenGenerated on its own would keep serving instructions
  // written before the import, which is exactly how the allergy went missing.
  useEffect(() => {
    const patientContextChanged =
      hasBeenGenerated &&
      generatedForPatientRef.current !== null &&
      generatedForPatientRef.current !== patientContextKey;

    if (
      autoGenerate &&
      (!hasBeenGenerated || patientContextChanged) &&
      !isGenerating &&
      transcript &&
      selectedDiagnoses.length > 0
    ) {
      const primaryDiagnosis = selectedDiagnoses[0];
      const includedTreatments = selectedTreatments.filter(t => !t.disabled);
      generatedForPatientRef.current = patientContextKey;
      const generate = patientContextChanged
        ? regenerateDischargeInstructions
        : generateDischargeInstructions;
      generate(transcript, primaryDiagnosis, includedTreatments, patientContext);
    }
  }, [autoGenerate, hasBeenGenerated, isGenerating, transcript, selectedDiagnoses, selectedTreatments, generateDischargeInstructions, regenerateDischargeInstructions, patientContext, patientContextKey]);

  const handleRegenerate = useCallback(() => {
    const primaryDiagnosis = selectedDiagnoses.length > 0 ? selectedDiagnoses[0] : null;
    const includedTreatments = selectedTreatments.filter(t => !t.disabled);
    generatedForPatientRef.current = patientContextKey;
    regenerateDischargeInstructions(transcript, primaryDiagnosis, includedTreatments, patientContext);
  }, [transcript, selectedDiagnoses, selectedTreatments, regenerateDischargeInstructions, patientContext, patientContextKey]);

  // Group instructions by category
  const groupedInstructions = React.useMemo(() => {
    const groups: Record<string, any[]> = {
      medication_general: [],
      medication_specific: [],
      activity: [],
      diet: [],
      followup: [],
      warning_signs: [],
      wound_care: [],
      other: [],
    };
    
    // Log instructions for debugging
    console.log('[DischargeStep] All instructions:', instructions);
    
    instructions.forEach(inst => {
      const category = inst.category || 'other';
      console.log('[DischargeStep] Processing instruction:', { id: inst.id, category, title: inst.title });
      
      if (groups[category]) {
        groups[category].push(inst);
      } else {
        groups.other.push(inst);
      }
    });
    
    console.log('[DischargeStep] Grouped instructions:', groups);
    
    return groups;
  }, [instructions]);

  const handleEditSection = useCallback((sectionKey: string, currentContent: string, title: string, description: string) => {
    setModalTitle(title);
    setModalDescription(description);
    setEditingSection(sectionKey);
    setEditedContent(currentContent);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (editingSection) {
      // Find the instruction for this section and update it
      const sectionInstructions = groupedInstructions[editingSection] || [];
      if (sectionInstructions.length > 0) {
        // Update the first instruction in this section
        updateInstructionProse(sectionInstructions[0].id, editedContent);
      }
    }
    setEditingSection(null);
    setEditedContent('');
  }, [editingSection, editedContent, groupedInstructions, updateInstructionProse]);

  const handleCancelEdit = useCallback(() => {
    setEditingSection(null);
    setEditedContent('');
  }, []);


  // Section configurations - matching LLM output categories
  const sections = [
    {
      key: 'medication_general',
      title: 'Medication General',
      color: theme.palette.primary.main,
      placeholder: "General medication safety and administration guidelines",
      description: "General instructions for taking medications safely and effectively.",
    },
    {
      key: 'medication_specific',
      title: 'Medication Specific',
      color: theme.palette.primary.main,
      placeholder: "Specific medication instructions",
      description: "Detailed instructions for each prescribed medication.",
    },
    {
      key: 'activity',
      title: 'Activity',
      color: theme.palette.info.main,
      placeholder: "Activity restrictions and recommendations",
      description: "What activities to avoid or limit, and when to return to normal activity.",
    },
    {
      key: 'diet',
      title: 'Diet',
      color: theme.palette.success.main,
      placeholder: "Dietary recommendations or restrictions",
      description: "What to eat and drink, and any foods to avoid.",
    },
    {
      key: 'followup',
      title: 'Followup',
      color: theme.palette.secondary.main,
      placeholder: "Follow-up instructions",
      description: "When and with whom the patient should follow up.",
    },
    {
      key: 'warning_signs',
      title: 'Warning Signs',
      color: theme.palette.error.main,
      placeholder: "Red flags that require immediate medical attention",
      description: "Symptoms that mean the patient should seek immediate medical care.",
    },
    {
      key: 'wound_care',
      title: 'Wound Care',
      color: theme.palette.warning.main,
      placeholder: "Instructions for wound care if applicable",
      description: "How to care for any wounds, incisions, or injuries.",
    },
    {
      key: 'other',
      title: 'Other',
      color: theme.palette.text.secondary,
      placeholder: "Other relevant instructions",
      description: "Any other important instructions for the patient.",
    },
  ];

  const getSectionContent = (sectionKey: string) => {
    const sectionInstructions = groupedInstructions[sectionKey] || [];
    if (sectionInstructions.length === 0) return '';
    return sectionInstructions.map(inst => inst.prose).join('\n\n');
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 900 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 1.5 }}>
          {heading?.title ?? "Discharge Instructions"}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {heading?.subtitle ??
          "Review and edit patient-friendly discharge instructions. Click edit to customize any section."}
        </Typography>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ width: '100%', mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRegenerate} startIcon={<RefreshRounded />}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {isGenerating && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Generating discharge instructions...
          </Typography>
        </Box>
      )}

      {/* Generated instructions */}
      {!isGenerating && hasBeenGenerated && (
        <>
          {/* Same right-aligned action row, and the same Export to PDF, as the Chart step. */}
          <Box display="flex" gap={1} justifyContent="flex-end" mb={2} flexWrap="wrap">
            {showSendToPatient && (
              <LoadingButton
                variant="contained"
                size="small"
                startIcon={<ForwardToInboxRounded />}
                onClick={handleSendToPatientClick}
                loading={sending}
                disabled={
                  !canSend || (importedPatient ? !onSendDischarge : !onCreateDischargeHandoff)
                }
              >
                Send to patient
              </LoadingButton>
            )}
            <LoadingButton
              variant="outlined"
              size="small"
              startIcon={<PictureAsPdfOutlined />}
              onClick={() => void handleExportPdf()}
              loading={exportingPdf}
              disabled={!canSend || !getPatientDischarge}
            >
              Export to PDF
            </LoadingButton>
          </Box>

          {sendSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSendSuccess(false)}>
              Discharge instructions sent to {patientLabel}
              {sendEmailed ? ' and they have been notified by email.' : '.'} They will appear
              in the patient's Discharge Instructions page.
            </Alert>
          )}

          {handoffClaimed && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setHandoffClaimed(false)}>
              The patient opened the discharge instructions on their phone.
            </Alert>
          )}

          {exportError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}

          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSendError(null)}>
              {sendError}
            </Alert>
          )}

          {/* Sections - only show sections with content */}
          <Box sx={{ minHeight: 400 }}>
            {sections.map((section) => {
              const content = getSectionContent(section.key);
              if (!content) return null;
              return (
                <SectionCard
                  key={section.key}
                  title={section.title}
                  content={content}
                  accentColor={section.color}
                  onEdit={() => handleEditSection(section.key, content, section.title, section.description)}
                />
              );
            })}
          </Box>

          {/* Source documents that backed the instructions */}
          <ClinicalReferenceSources
            clinicalReferences={clinicalReferences}
            embeddedDocuments={embeddedDocuments}
          />
        </>
      )}

      {/* Edit Modal */}
      <Dialog
        open={editingSection !== null}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" component="div">
            {modalTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {modalDescription}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={12}
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            variant="outlined"
            placeholder={sections.find(s => s.key === editingSection)?.placeholder}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>
            Close
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <DischargeHandoffDialog
        open={handoffOpen}
        onClose={() => setHandoffOpen(false)}
        onCreateHandoff={onCreateDischargeHandoff}
        onClaimed={handleHandoffClaimed}
      />

      <ConfirmDialog
        open={sendConfirmOpen}
        onClose={() => setSendConfirmOpen(false)}
        title="Send discharge instructions?"
        message={`This sends the discharge instructions to ${patientLabel}. They will be able to read them from their Discharge Instructions page.`}
        confirmLabel="Send"
        onConfirm={handleSendDischarge}
        loading={sending}
      />
    </Box>
  );
};

export default DischargeStep;
